import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  SimpleGrid,
  Stat,
  Card,
  Table,
  Button,
  useDisclosure,
  Dialog,
  Input,
  Text,
  Badge,
  InputGroup,
  RadioGroup,
  HStack,
  Field,
} from "@chakra-ui/react";
import { FiSearch } from "react-icons/fi";
import api from "../lib/api";
import { toaster } from "../components/ui/toaster";

interface ReviewStats {
  pending_count: number;
  total_distinct_pending: number;
}

interface ReviewItem {
  product_description: string;
  frequency: number;
}

interface MasterDrug {
  id: number;
  brand_name: string;
  generic_name: string;
  drug_group: string;
}

const ReviewPage: React.FC = () => {
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<ReviewItem | null>(null);
  const { open, onOpen, onClose } = useDisclosure();

  const [linkMode, setLinkMode] = useState<"existing" | "new">("existing");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<MasterDrug[]>([]);
  const [selectedMasterId, setSelectedMasterId] = useState<number | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // New drug form
  const [newDrugName, setNewDrugName] = useState("");
  const [newDrugGroup, setNewDrugGroup] = useState("");

  const fetchData = async () => {
    try {
      // Run in parallel for better performance
      const [statsRes, itemsRes] = await Promise.all([
        api.get("/review/stats"),
        api.get("/review/pending?limit=50"),
      ]);

      setStats(statsRes.data.data ? statsRes.data.data : statsRes.data);
      setItems(itemsRes.data.data ? itemsRes.data.data : itemsRes.data);
    } catch (err) {
      console.error(err);
      toaster.create({
        title: "Failed to load review data",
        type: "error",
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenReview = (item: ReviewItem) => {
    setSelectedItem(item);
    setNewDrugName(item.product_description); // Default to raw name
    setSearchQuery(""); // Start with empty search
    setLinkMode("existing");
    setSearchResults([]);
    setSelectedMasterId(null);
    setSearchError(null);
    onOpen();
  };

  const searchMasterDrugs = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      setSearchError(null);
      return;
    }
    
    setIsSearching(true);
    setSearchError(null);
    
    try {
      const res = await api.get(`/admin/master-drugs?search=${query}&limit=20`);
      
      // Handle different response structures
      let results: MasterDrug[] = [];
      
      if (res.data.items && Array.isArray(res.data.items)) {
        // Direct structure: { items: [...] }
        results = res.data.items;
      } else if (res.data.data && res.data.data.items && Array.isArray(res.data.data.items)) {
        // Nested structure: { data: { items: [...] } }
        results = res.data.data.items;
      } else if (Array.isArray(res.data.data)) {
        // Array structure: { data: [...] }
        results = res.data.data;
      } else if (Array.isArray(res.data)) {
        // Direct array: [...]
        results = res.data;
      }
      
      setSearchResults(results);
      
      if (results.length === 0) {
        setSearchError("No drugs found. Try a different search term.");
      }
    } catch (err) {
      console.error(err);
      setSearchError("Search failed. Please try again.");
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    if (linkMode === "existing" && searchQuery.length >= 2) {
      const timer = setTimeout(() => {
        searchMasterDrugs(searchQuery);
      }, 500); // 500ms debounce
      
      return () => clearTimeout(timer);
    } else if (searchQuery.length < 2) {
      setSearchResults([]);
      setSearchError(null);
    }
  }, [searchQuery, linkMode]);

  const handleSubmit = async () => {
    if (!selectedItem) return;

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: any = {
        raw_name: selectedItem.product_description,
      };

      if (linkMode === "existing") {
        if (!selectedMasterId) {
          toaster.create({
            title: "Selection Required",
            description: "Please select a drug from the search results",
            type: "warning",
          });
          return;
        }
        payload.master_drug_id = selectedMasterId;
      } else {
        if (!newDrugName.trim()) {
          toaster.create({
            title: "Generic Name Required",
            description: "Please enter the generic drug name",
            type: "warning",
          });
          return;
        }
        if (!newDrugGroup.trim()) {
          toaster.create({
            title: "Drug Group Required",
            description: "Please enter the drug group/category",
            type: "warning",
          });
          return;
        }
        payload.create_new_name = newDrugName.trim();
        payload.drug_group = newDrugGroup.trim();
      }

      await api.post("/review/link", payload);
      toaster.create({
        title: "Success!",
        description: `Product linked successfully to ${linkMode === "existing" ? "existing" : "new"} master drug`,
        type: "success",
      });
      onClose();
      fetchData(); // Refresh list
    } catch (err) {
      console.error(err);
      toaster.create({
        title: "Link Failed",
        description: "Unable to link product. Please try again.",
        type: "error",
      });
    }
  };

  return (
    <Box p={5}>
      <Heading size="lg" mb={6}>
        Normalization Review Queue
      </Heading>

      {/* Stats */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6} mb={8}>
        <Card.Root>
          <Card.Body>
            <Stat.Root>
              <Stat.Label>Pending Review</Stat.Label>
              <Stat.ValueText>
                {stats?.total_distinct_pending.toLocaleString() || 0}
              </Stat.ValueText>
              <Stat.HelpText>Unique product descriptions</Stat.HelpText>
            </Stat.Root>
          </Card.Body>
        </Card.Root>
        <Card.Root>
          <Card.Body>
            <Stat.Root>
              <Stat.Label>System Status</Stat.Label>
              <Stat.ValueText>Active</Stat.ValueText>
              <Stat.HelpText>Review Logic Enabled</Stat.HelpText>
            </Stat.Root>
          </Card.Body>
        </Card.Root>
      </SimpleGrid>

      {/* List */}
      {/* <Card.Root width="100%">
        <Card.Body> */}
      <Box overflowX="auto">
        <Table.Root variant="outline">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Product Description</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="end">
                Frequency (Tx Count)
              </Table.ColumnHeader>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
              <Table.ColumnHeader>Action</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.map((item, idx) => (
              <Table.Row key={idx}>
                <Table.Cell fontWeight="medium">
                  {item.product_description}
                </Table.Cell>
                <Table.Cell textAlign="end">{item.frequency}</Table.Cell>
                <Table.Cell>
                  <Badge colorPalette="orange">Review Needed</Badge>
                </Table.Cell>
                <Table.Cell>
                  <Button
                    size="sm"
                    colorPalette="blue"
                    onClick={() => handleOpenReview(item)}>
                    Fix / Link
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
            {items.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={4} textAlign="center">
                  No items pending review.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Root>
      </Box>
      {/* </Card.Body>
      </Card.Root> */}

      {/* Modal / Dialog */}
      <Dialog.Root
        open={open}
        onOpenChange={(e) => (e.open ? onOpen() : onClose())}
        size="xl">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Normalize Product</Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body>
              <Box mb={4}>
                <Text fontWeight="bold" mb={1}>Raw Product Name:</Text>
                <Text fontSize="lg" color="gray.700" p={2} bg="gray.50" borderRadius="md">
                  {selectedItem?.product_description}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Link this to a master drug in the index or create a new one
                </Text>
              </Box>

              <RadioGroup.Root
                value={linkMode}
                onValueChange={(details) => {
                  const newMode = details.value as "existing" | "new";
                  setLinkMode(newMode);
                  // Reset states when switching modes
                  setSelectedMasterId(null);
                  setSearchError(null);
                  if (newMode === "existing") {
                    setSearchQuery("");
                    setSearchResults([]);
                  }
                }}>
                <HStack gap={6}>
                  <RadioGroup.Item value="existing">
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemIndicator />
                    <RadioGroup.ItemText>
                      Link to Existing Master Drug
                    </RadioGroup.ItemText>
                  </RadioGroup.Item>
                  <RadioGroup.Item value="new">
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemIndicator />
                    <RadioGroup.ItemText>
                      Create New Master Drug
                    </RadioGroup.ItemText>
                  </RadioGroup.Item>
                </HStack>
              </RadioGroup.Root>

              <Box mt={4}>
                {linkMode === "existing" ? (
                  <Box>
                    <Field.Root mb={3}>
                      <Field.Label fontWeight="medium" mb={2}>
                        Search Master Drug Index
                      </Field.Label>
                      <InputGroup
                        startElement={<FiSearch color="gray" />}>
                        <Input
                          id="search-master-drugs"
                          placeholder="Type brand or generic name (min 2 chars)..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          autoFocus
                        />
                      </InputGroup>
                    {isSearching && (
                      <Text fontSize="xs" color="blue.500" mt={1}>
                        Searching...
                      </Text>
                    )}
                    {searchError && (
                      <Text fontSize="xs" color="red.500" mt={1}>
                        {searchError}
                      </Text>
                    )}
                    </Field.Root>

                  <Box
                    maxH="280px"
                    overflowY="auto"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    bg="gray.50">
                    {searchResults.length > 0 ? (
                      searchResults.map((drug) => (
                        <Box
                          key={drug.id}
                          p={3}
                          _hover={{ bg: "blue.50", cursor: "pointer" }}
                          bg={selectedMasterId === drug.id ? "blue.100" : "white"}
                          onClick={() => setSelectedMasterId(drug.id)}
                          borderBottom="1px solid"
                          borderColor="gray.200"
                          transition="all 0.2s">
                          <HStack justify="space-between">
                            <Box flex="1">
                              <Text fontWeight="bold" fontSize="sm">
                                {drug.brand_name || "(No Brand)"}
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {drug.generic_name}
                              </Text>
                            </Box>
                            <Badge colorPalette="purple" size="sm">
                              {drug.drug_group}
                            </Badge>
                          </HStack>
                        </Box>
                      ))
                    ) : (
                      <Box p={6} textAlign="center">
                        {searchQuery.length < 2 ? (
                          <Text color="gray.500" fontSize="sm">
                            Type at least 2 characters to search
                          </Text>
                        ) : isSearching ? (
                          <Text color="gray.500" fontSize="sm">
                            Searching...
                          </Text>
                        ) : (
                          <Text color="gray.500" fontSize="sm">
                            No drugs found - try a different search term
                          </Text>
                        )}
                      </Box>
                    )}
                  </Box>
                  
                  {selectedMasterId && (
                    <Box mt={2} p={2} bg="blue.50" borderRadius="md">
                      <Text fontSize="xs" color="blue.700">
                        ✓ Drug selected - click "Link Product" below to confirm
                      </Text>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box>
                  <Field.Root mb={3}>
                    <Field.Label fontWeight="medium" mb={2}>
                      Generic Name (Standardized) *
                    </Field.Label>
                    <Input
                      id="new-drug-generic-name"
                      value={newDrugName}
                      onChange={(e) => setNewDrugName(e.target.value)}
                      placeholder="e.g., Paracetamol, Amoxicillin"
                      autoFocus
                    />
                  </Field.Root>
                  <Field.Root mb={3}>
                    <Field.Label fontWeight="medium" mb={2}>
                      Drug Group / Category *
                    </Field.Label>
                    <Input
                      id="new-drug-group"
                      value={newDrugGroup}
                      onChange={(e) => setNewDrugGroup(e.target.value)}
                      placeholder="e.g., Analgesic, Antibiotic"
                    />
                  </Field.Root>
                  <Box p={3} bg="yellow.50" borderRadius="md" borderLeft="3px solid" borderColor="yellow.400">
                    <Text fontSize="xs" color="gray.700">
                      <strong>Note:</strong> This will create a new entry in the Master Drug Index. 
                      Make sure this drug doesn't already exist by searching first.
                    </Text>
                  </Box>
                </Box>
              )}
              </Box>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button 
                colorPalette="blue" 
                onClick={handleSubmit}
                disabled={
                  linkMode === "existing" 
                    ? !selectedMasterId 
                    : !newDrugName.trim() || !newDrugGroup.trim()
                }>
                {linkMode === "existing" ? "Link to Selected Drug" : "Create & Link Drug"}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Box>
  );
};

export default ReviewPage;
